import React, { useEffect, useState } from "react";
import { Modal, Typography, Space, Dropdown } from "antd";
import { MdOutlineRemoveRedEye, MdOutlineEdit, MdOutlineDelete } from "react-icons/md";
import { TbDotsVertical } from "react-icons/tb";
import SearchInput from "../Forms/SearchInput";
import { useApiCall } from "../../services/api";
import { useSelector } from "react-redux";
import PrimaryButton from "../Forms/PrimaryButton";
import { useGlobalModal } from "../../context/SuccessModalProvider";
import CustomBadge from "../Charts/CustomBadge";
import DataTable from "../Tables/Datatable";
import ConfirmModal from "./ConfirmModal";
import AddEditBrochureModal from "./AddEditBrochureModal";
import ViewBrochureModal from "./ViewBrochureModal";
import { PERMISSION_IDS } from "../../constant";

const { Title } = Typography;

export default function BrochuresModal({ isOpen, setIsOpen, title = "Brochure List", description }) {
  const { postApi, loading, getApi } = useApiCall();
  const [allBrochures, setAllBrochures] = useState([]);
  const [filteredBrochures, setFilteredBrochures] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [modalAction, setModalAction] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [viewRecord, setViewRecord] = useState(null);

  const [isAddEditOpen, setIsAddEditOpen] = useState(false);
  const [editRecord, setEditRecord] = useState(null);

  const { user_id, is_sales_person, permission_ids } = useSelector((state) => state.auth);

  const canManageBrochures = !is_sales_person || permission_ids.includes(PERMISSION_IDS.BROCHURE_MANAGEMENT);

  useEffect(() => {
    if (isOpen) {
      fetchBrochures();
      setSearchText("");
    }
  }, [isOpen]);

  const fetchBrochures = async () => {
    try {
      const response = await getApi("sales/brochure/list");
      const formatted = (response.data || []).map((item, index) => ({
        ...item,
        status: item.status === "Active" ? "Active" : "Disabled",
        key: index + 1,
      }));
      setAllBrochures(formatted);
      setFilteredBrochures(formatted);
    } catch (error) {
      console.error(error);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);

    const sanitizedValue = value?.toLowerCase()?.trim();

    const filtered = allBrochures.filter((b) => {
      const nameMatch = (b.name || b.brochure_name)?.toLowerCase().includes(sanitizedValue);
      const statusMatch = b.status?.toLowerCase().includes(sanitizedValue);

      return nameMatch || statusMatch;
    });

    setFilteredBrochures(filtered);
  };

  const handleView = (item) => {
    setViewRecord(item);
    setIsViewOpen(true);
  };

  const handleEdit = (item) => {
    setEditRecord(item);
    setIsAddEditOpen(true);
  };

  const handleDelete = (item) => {
    setSelectedRecord(item);
    setModalAction("DELETE_BROCHURE");
  };

  const handleConfirmAction = async () => {
    try {
      if (modalAction === "DELETE_BROCHURE") {
        const response = await postApi("sales/brochure/delete", { brochure_id: selectedRecord?.brochure_id }, true);

        if (response?.status) {
          setModalAction(null);
          setSelectedRecord(null);
          fetchBrochures();
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleAddEditSubmit = async (payload, existingData) => {
    try {
      let response;
      const formData = new FormData();
      formData.append("name", payload.name);
      formData.append("status", payload.status);

      if (payload.pdf_file) formData.append("brochure_pdf", payload.pdf_file);
      if (payload.image_file) formData.append("brochure_picture", payload.image_file);

      if (existingData) {
        formData.append("brochure_id", existingData.brochure_id);
        response = await postApi("sales/brochure/update", formData, true);
      } else {
        response = await postApi("sales/brochure/add", formData, true);
      }

      if (response?.status) {
        setIsAddEditOpen(false);
        fetchBrochures();
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getDropdownMenuItems = (item) => {
    return {
      items: [
        {
          key: "view",
          label: (
            <Space className="text-[#A33434] font-semibold">
              <div className="bg-[#A33434] p-1.5 flex items-center justify-center rounded-full text-[#ffffff]">
                <MdOutlineRemoveRedEye size={14} />
              </div>
              View Brochure
            </Space>
          ),
        },
        {
          key: "edit",
          label: (
            <Space className="text-[#A33434] font-semibold">
              <div className="bg-[#A33434] p-1.5 flex items-center justify-center rounded-full text-[#ffffff]">
                <MdOutlineEdit size={14} />
              </div>
              Edit Brochure
            </Space>
          ),
        },
        {
          key: "delete",
          label: (
            <Space className="text-[#A33434] font-semibold">
              <div className="bg-[#A33434] p-1.5 flex items-center justify-center rounded-full text-[#ffffff]">
                <MdOutlineDelete size={14} />
              </div>
              Delete Brochure
            </Space>
          ),
        },
      ],
      onClick: ({ key, domEvent }) => {
        domEvent.stopPropagation();

        switch (key) {
          case "view":
            handleView(item);
            break;
          case "edit":
            handleEdit(item);
            break;
          case "delete":
            handleDelete(item);
            break;
          default:
            break;
        }
      },
    };
  };

  const columns = [
    {
      title: <span className="text-left font-medium text-gray-700">#</span>,
      render: (_, __, index) => <span className="font-normal text-black">{index + 1}</span>,
      width: 50,
    },
    {
      title: <span className="text-gray-900 text-left font-normal">Name</span>,
      dataIndex: "name",
      ellipsis: true,
      width: canManageBrochures ? 200 : 280,
    },
    {
      title: <span className="text-gray-900 text-left font-normal">Status</span>,
      dataIndex: "status",
      render: (value) => <CustomBadge variant={value === "Active" ? "success" : "danger"} text={value} />,
    },
    {
      title: <span className="text-gray-900 text-center font-normal">Actions</span>,
      align: "center",
      width: canManageBrochures ? undefined : 80,
      render: (_, item) => (
        <span className="text-gray-900 flex justify-center items-center">
          {!canManageBrochures ? (
            <button onClick={(e) => getDropdownMenuItems(item)?.onClick({ key: "view", domEvent: e })}>
              {getDropdownMenuItems(item)?.items[0]?.label}
            </button>
          ) : (
            <Dropdown menu={getDropdownMenuItems(item)} trigger={["click"]} overlayClassName="custom-dropdown-sales-menu">
              <Typography.Link
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <Space>
                  <TbDotsVertical className="text-red-700" size={18} />
                </Space>
              </Typography.Link>
            </Dropdown>
          )}
        </span>
      ),
    },
  ];

  return (
    <>
      <Modal className="form-css w-full max-w-3xl" open={isOpen} footer={null} onCancel={() => setIsOpen(false)} width={600}>
        <div className="flex justify-between items-center mb-3">
          <Title level={2} className="text-3xl font-semibold text-gray-800 font-abhaya m-0">
            {title}
          </Title>
        </div>

        {description && (
          <div className="text-left">
            <p className="text-gray-500 text-sm pb-2">{description}</p>
          </div>
        )}

        <div className="flex items-center gap-4 mb-4">
          <div className="flex-1">
            <SearchInput value={searchText} onChange={handleSearchChange} placeholder="Search for brochures" />
          </div>
          {canManageBrochures && (
            <PrimaryButton
              btnType="secondary"
              text="Add Brochure"
              onClick={() => {
                setEditRecord(null);
                setIsAddEditOpen(true);
              }}
            />
          )}
        </div>

        <DataTable
          rowKey="key"
          columns={columns}
          dataSource={filteredBrochures}
          pagination={false}
          Showpagination={false}
          customEmptySection="Sorry, no brochures found"
          className="!px-0"
          rootClassName="transparent-table"
          rowClassName={"hover:bg-[#FBEEEC]"}
          disableHover
          scroll={null}
        />
      </Modal>

      <ConfirmModal
        actionKey={modalAction}
        record={selectedRecord}
        open={modalAction === "DELETE_BROCHURE"}
        onCancel={() => {
          setModalAction(null);
          setSelectedRecord(null);
        }}
        onConfirm={handleConfirmAction}
        loading={loading}
      />

      <AddEditBrochureModal
        isOpen={isAddEditOpen}
        closeModal={() => setIsAddEditOpen(false)}
        data={editRecord}
        onConfirm={handleAddEditSubmit}
        loading={loading}
      />

      <ViewBrochureModal isOpen={isViewOpen} closeModal={() => setIsViewOpen(false)} data={viewRecord} />
    </>
  );
}
